import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { fitTransform, effectiveLayers, designSizeFor, cropsToOverlay, artboardRect } from '@/util/overlayGeometry'
import { renderOverlayStage } from '@/util/overlayRenderer'

interface OverlayRow {
  id: number
  name: string
  layers: string | any[]
  aspect_ratio: string
  reference_width: number
  reference_height: number
  crop_to_overlay?: number | boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LiveCameraCapture',
  props: {
    show: { type: Boolean },
    overlays: { default: () => [] },
    overlayId: { default: null },
    resolveText: { type: Function, default: undefined }
  },
  emits: ["close", "capture", "use-native"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'capture', payload: { file: File; overlayId: number | null }): void
  (e: 'use-native'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { show: boolean, overlays: OverlayRow[], overlayId: number | null, resolveText: (content: string) => Promise<string> | string }

// Live overlay camera. The browser's camera stream shows inside the console
// with the chosen overlay drawn over it at the same contain fit the bake uses,
// so staff frame the group around the overlay before the shutter. The captured
// frame is a clean JPEG at stream resolution; the page bakes the overlay through
// its normal path, which lands it exactly where the preview showed it.




const stage = ref<HTMLElement | null>(null)
const videoEl = ref<HTMLVideoElement | null>(null)
const overlayEl = ref<HTMLCanvasElement | null>(null)
const selectedOverlayId = ref<number | ''>('')
const facing = ref<'environment' | 'user'>('environment')
const ready = ref(false)
const capturing = ref(false)
const error = ref('')

let stream: MediaStream | null = null
// The overlay rendered over the whole video frame (in design units), like the
// bake does, so text that runs past the designed area is not clipped. `aspect`
// is the video aspect it was rendered for; a rotation re-renders it. With crop
// on, the stage is the artboard only and layout() dims what the bake cuts away.
let stageImage: { canvas: HTMLCanvasElement; aspect: number | null; crop: boolean } | null = null
let renderToken = 0

const selectedOverlay = computed<OverlayRow | null>(() => {
  if (selectedOverlayId.value === '') return null
  return props.overlays.find(o => o.id === selectedOverlayId.value) || null
})

async function start() {
  error.value = ''
  stop()
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    error.value = 'This browser cannot share its camera.'
    return
  }
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: facing.value }, width: { ideal: 4096 }, height: { ideal: 2160 } },
      audio: false,
    })
  } catch (e: any) {
    const name = e && e.name
    error.value = name === 'NotAllowedError' || name === 'SecurityError'
      ? 'Camera access was blocked. Allow it in the browser settings, or use the system camera.'
      : 'The camera could not be started.'
    return
  }
  const video = videoEl.value
  if (!video || !props.show) {
    stop()
    return
  }
  video.srcObject = stream
  video.onloadedmetadata = () => {
    ready.value = true
    layout()
  }
  // The stream's dimensions change after a rotation, later than the window
  // resize event; the video element reports it with its own resize event.
  video.onresize = () => layout()
  try { await video.play() } catch (e) { /* autoplay refused; the first tap on the shutter plays */ }
}

function stop() {
  if (stream) {
    stream.getTracks().forEach(t => t.stop())
    stream = null
  }
  if (videoEl.value) {
    videoEl.value.onresize = null
    videoEl.value.srcObject = null
  }
  ready.value = false
}

function flip() {
  facing.value = facing.value === 'environment' ? 'user' : 'environment'
  start()
}

async function renderOverlay() {
  const token = ++renderToken
  const overlay = selectedOverlay.value
  if (!overlay) {
    stageImage = null
    layout()
    return
  }
  const eff = effectiveLayers(overlay, null)
  const video = videoEl.value
  const vw = video ? video.videoWidth : 0
  const vh = video ? video.videoHeight : 0
  let frame: { width: number; height: number } | null = null
  let offset: { x: number; y: number } | null = null
  let aspect: number | null = null
  const crop = cropsToOverlay(overlay)
  if (vw && vh) {
    aspect = vw / vh
    // Crop on: the stage is the artboard only, so a layer past the designed
    // area is cut off here exactly as the bake cuts it.
    if (!crop) {
      const design = designSizeFor(overlay.aspect_ratio)
      const fit = fitTransform(design, vw, vh)
      frame = { width: vw / fit.scale, height: vh / fit.scale }
      offset = { x: fit.offsetX / fit.scale, y: fit.offsetY / fit.scale }
    }
  }
  const image = await renderOverlayStage({
    layers: eff.layers,
    aspectRatio: overlay.aspect_ratio,
    referenceWidth: eff.referenceWidth,
    referenceHeight: eff.referenceHeight,
    resolveText: props.resolveText,
    frame,
    offset,
  })
  if (token !== renderToken) return
  stageImage = { canvas: image.canvas, aspect, crop }
  layout()
}

// Size the overlay canvas to the displayed video rectangle (contain fit inside
// the stage box) and draw the stage image with the bake's fit transform.
function layout() {
  const video = videoEl.value
  const canvas = overlayEl.value
  const box = stage.value
  if (!video || !canvas || !box) return
  const vw = video.videoWidth
  const vh = video.videoHeight
  const bw = box.clientWidth
  const bh = box.clientHeight
  if (!vw || !vh || !bw || !bh) return
  const s = Math.min(bw / vw, bh / vh)
  const rw = Math.round(vw * s)
  const rh = Math.round(vh * s)
  const dpr = window.devicePixelRatio || 1
  canvas.style.width = rw + 'px'
  canvas.style.height = rh + 'px'
  canvas.style.left = Math.round((bw - rw) / 2) + 'px'
  canvas.style.top = Math.round((bh - rh) / 2) + 'px'
  canvas.width = Math.round(rw * dpr)
  canvas.height = Math.round(rh * dpr)
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  if (!stageImage) return
  // Rendered for another aspect (or before the stream had dimensions): re-render.
  const aspect = vw / vh
  if (stageImage.aspect === null || Math.abs(stageImage.aspect - aspect) > 0.01) {
    renderOverlay()
    return
  }
  if (!stageImage.crop || !selectedOverlay.value) {
    ctx.drawImage(stageImage.canvas, 0, 0, canvas.width, canvas.height)
    return
  }
  // Crop on: the overlay covers its own area and everything the bake cuts away is dimmed.
  const design = designSizeFor(selectedOverlay.value.aspect_ratio)
  const k = canvas.width / vw
  const art = artboardRect(design, vw, vh)
  const ax = art.x * k
  const ay = art.y * k
  const aw = art.width * k
  const ah = art.height * k
  ctx.drawImage(stageImage.canvas, ax, ay, aw, ah)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
  ctx.beginPath()
  ctx.rect(0, 0, canvas.width, canvas.height)
  ctx.rect(ax, ay, aw, ah)
  ctx.fill('evenodd')
}

function capture() {
  const video = videoEl.value
  if (!video || !ready.value || capturing.value) return
  capturing.value = true
  const canvas = document.createElement('canvas')
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    capturing.value = false
    return
  }
  // Drawn as the sensor sees it: the front camera preview is mirrored on screen only.
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
  canvas.toBlob((blob) => {
    capturing.value = false
    if (!blob) return
    const file = new File([blob], 'live-' + Date.now() + '.jpg', { type: 'image/jpeg' })
    emit('capture', { file, overlayId: selectedOverlayId.value === '' ? null : selectedOverlayId.value })
  }, 'image/jpeg', 0.92)
}

function onKeydown(e: KeyboardEvent) {
  if (!props.show) return
  if (e.key === 'Escape') emit('close')
}

function onResize() {
  layout()
}

// On a rotation Android keeps the stream in the old orientation until it is
// renegotiated, which leaves a landscape band in a portrait box. Restart the
// stream once the window has settled in the new orientation.
let orientationTimer: ReturnType<typeof setTimeout> | null = null
function onOrientationChange() {
  if (orientationTimer) clearTimeout(orientationTimer)
  orientationTimer = setTimeout(() => {
    orientationTimer = null
    if (props.show && !error.value) start()
  }, 350)
}

watch(() => props.show, async (open) => {
  if (open) {
    selectedOverlayId.value = typeof props.overlayId === 'number' ? props.overlayId : ''
    facing.value = 'environment'
    document.addEventListener('keydown', onKeydown)
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onOrientationChange)
    await nextTick()
    start()
    renderOverlay()
  } else {
    document.removeEventListener('keydown', onKeydown)
    window.removeEventListener('resize', onResize)
    window.removeEventListener('orientationchange', onOrientationChange)
    if (orientationTimer) clearTimeout(orientationTimer)
    stop()
    stageImage = null
    error.value = ''
    capturing.value = false
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', onResize)
  window.removeEventListener('orientationchange', onOrientationChange)
  if (orientationTimer) clearTimeout(orientationTimer)
  stop()
})

return { __sfc: true,props, emit, stage, videoEl, overlayEl, selectedOverlayId, facing, ready, capturing, error, stream, stageImage, renderToken, selectedOverlay, start, stop, flip, renderOverlay, layout, capture, onKeydown, onResize, orientationTimer, onOrientationChange }
}

})